import React from 'react';
import MetricComparisonBarChart from '@components/charts/MetricComparisonBarChart';
import MetricComparisonTable from './MetricComparisonTable';
import type { MetricComparisonCardData } from '@app-types/comparisonMetric';
import './MetricComparisonCard.less';

export interface MetricComparisonCardProps {
  data: MetricComparisonCardData;
}

/**
 * Financial metric card used by Sales / Ordinary Profit / Net Income screens.
 *
 * Desktop: horizontal layout (chart panel left, table panel right), 241px tall.
 * Mobile: stacked (chart panel top, table panel bottom).
 *
 * The chart panel contains a price block (code + name / price + change) above
 * the bar chart. See kiro_sales_revenue_task.md §6.
 */
const MetricComparisonCard: React.FC<MetricComparisonCardProps> = ({ data }) => {
  const toneClass =
    data.changeTone === 'up'
      ? 'comparison-metric-card__change--up'
      : data.changeTone === 'down'
      ? 'comparison-metric-card__change--down'
      : 'comparison-metric-card__change--neutral';

  return (
    <article className="comparison-metric-card" data-node-id={data.nodeId}>
      <div className="comparison-metric-card__chart-panel">
        <div className="comparison-metric-card__price">
          <div className="comparison-metric-card__price-row">
            <span className="comparison-metric-card__code">{data.code}</span>
            <span className="comparison-metric-card__name">{data.name}</span>
          </div>
          <div className="comparison-metric-card__price-row">
            <span className="comparison-metric-card__price-value">{data.price}</span>
            <span className={`comparison-metric-card__change ${toneClass}`}>{data.change}</span>
          </div>
        </div>
        <MetricComparisonBarChart points={data.chartPoints} />
      </div>
      <div className="comparison-metric-card__table-panel">
        <MetricComparisonTable valueHeader={data.valueHeader} rows={data.tableRows} />
      </div>
    </article>
  );
};

export default MetricComparisonCard;
